import axios, { AxiosResponse } from 'axios';
import { ApiResponse, User, VOD, DashboardStats, TwitchVOD } from '../shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Only redirect to login if we're not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export interface AuthResponse {
  user: User;
  token: string;
}

export interface UploadInitiateRequest {
  filename: string;
  fileSize: number;
  contentType: string;
}

export interface UploadInitiateResponse {
  vodId: string;
  uploadUrl: string;
  maxFileSize: number;
}

export interface UploadStatusResponse {
  vodId: string;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  filename: string;
  fileSize: number;
  uploadedAt: string;
}

export interface VODsResponse {
  vods: VOD[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface TwitchVODsResponse {
  vods: (TwitchVOD & { isBackedUp: boolean; backupSize: string; chatBackupSize?: string })[];
  pagination?: {
    cursor: string;
  };
}

export interface SyncResponse {
  syncedCount: number;
  totalAvailable: number;
  results: Array<{
    vodId?: string;
    twitchVodId: string;
    status: 'success' | 'error';
  }>;
  message: string;
}

export interface StorageBreakdown {
  bySource: Array<{
    source: string;
    count: number;
    size: number;
    sizeFormatted: string;
  }>;
  byStatus: Array<{
    status: string;
    count: number;
    size: number;
    sizeFormatted: string;
  }>;
  byTier: Array<{
    tier: string;
    count: number;
    size: number;
    sizeFormatted: string;
  }>;
}

// Auth API
export const authApi = {
  login: (): Promise<AxiosResponse<ApiResponse<{ authUrl: string; state: string }>>> =>
    api.get('/auth/login'),

  callback: (code: string, state: string): Promise<AxiosResponse<ApiResponse<AuthResponse>>> =>
    api.post('/auth/callback', { code, state }),

  me: (): Promise<AxiosResponse<ApiResponse<User>>> =>
    api.get('/auth/me'),

  logout: (): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.post('/auth/logout'),

  refresh: (): Promise<AxiosResponse<ApiResponse<{ token: string }>>> =>
    api.post('/auth/refresh'),
};

// Upload API
export const uploadApi = {
  initiate: (data: UploadInitiateRequest): Promise<AxiosResponse<ApiResponse<UploadInitiateResponse>>> =>
    api.post('/api/upload/initiate', data),

  upload: (vodId: string, file: File, onProgress?: (progress: number) => void): Promise<AxiosResponse<ApiResponse<{ vodId: string; status: string; message: string }>>> =>
    api.put(`/api/upload/${vodId}`, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    }),

  getStatus: (vodId: string): Promise<AxiosResponse<ApiResponse<UploadStatusResponse>>> =>
    api.get(`/api/upload/${vodId}/status`),
};

// VODs API
export const vodsApi = {
  getVODs: (page = 1, limit = 20, source?: string): Promise<AxiosResponse<ApiResponse<VODsResponse>>> =>
    api.get('/api/vods', { params: { page, limit, source } }),

  getVOD: (vodId: string): Promise<AxiosResponse<ApiResponse<VOD>>> =>
    api.get(`/api/vods/${vodId}`),

  getTwitchVODs: (after?: string): Promise<AxiosResponse<ApiResponse<TwitchVODsResponse>>> =>
    api.get('/api/vods/twitch/available', { params: { after } }),

  backupTwitchVOD: (twitchVodId: string): Promise<AxiosResponse<ApiResponse<{ vodId: string; twitchVodId: string; status: string; message: string }>>> =>
    api.post(`/api/vods/twitch/${twitchVodId}/backup`),

  syncVODs: (): Promise<AxiosResponse<ApiResponse<SyncResponse>>> =>
    api.post('/api/vods/sync'),

  deleteVOD: (vodId: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> =>
    api.delete(`/api/vods/${vodId}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: (): Promise<AxiosResponse<ApiResponse<DashboardStats & { totalStorageUsedFormatted: string; dataInBytesFormatted: string; dataOutBytesFormatted: string }>>> =>
    api.get('/api/dashboard/stats'),

  getActivity: (limit = 10): Promise<AxiosResponse<ApiResponse<{ recentVods: Array<VOD & { fileSizeFormatted: string }>; total: number }>>> =>
    api.get('/api/dashboard/activity', { params: { limit } }),

  getStorageBreakdown: (): Promise<AxiosResponse<ApiResponse<StorageBreakdown>>> =>
    api.get('/api/dashboard/storage'),

  refreshStats: (): Promise<AxiosResponse<ApiResponse<DashboardStats & { totalStorageUsedFormatted: string; dataInBytesFormatted: string; dataOutBytesFormatted: string; message: string }>>> =>
    api.post('/api/dashboard/refresh'),
};

export default api;